import {BaseApi} from './BaseApi'

/**
 * Request body for updating applicant cleanup date
 */
export interface UpdateCleanupDateRequest {
  /** The new cleanup date to set for the applicant (ISO date-time string) */
  lastCleanUpDate: string
}

/**
 * Response from get data cleanup totals API
 */
export interface DataCleanupTotalsResponse {
  thirtyDayRangeCount: number
  sixtyDayRangeCount: number
  ninetyDayRangeCount: number
  ninetyPlusDayRangeCount: number
  totalCount: number
}

/**
 * Response from update cleanup API
 */
export interface UpdateCleanupDateResponse {
  id: string
  lastCleanUpDate: string
  message?: string
}

/**
 * Data Cleanup API Service
 *
 * Provides methods for managing applicant data cleanup operations.
 * Uses the new `/applicants/update-cleanup/{id}` endpoint for updating cleanup dates.
 */
class DataCleanupApiService extends BaseApi {
  /**
   * Update applicant cleanup date with agency validation
   *
   * Updates the lastCleanUpDate field of an applicant after validating agency access.
   *
   * @param applicantId - The ID of the applicant to update
   * @param lastCleanUpDate - The new cleanup date to set (ISO date-time string)
   * @returns Promise with the updated applicant data
   *
   * @example
   * ```typescript
   * await dataCleanupApi.updateApplicantCleanupDate(
   *   'applicant-123',
   *   new Date().toISOString()
   * )
   * ```
   */
  async updateApplicantCleanupDate(
    applicantId: string,
    lastCleanUpDate: string,
  ): Promise<UpdateCleanupDateResponse> {
    try {
      const response = await this.put<UpdateCleanupDateResponse>(
        `/applicants/update-cleanup/${applicantId}`,
        {lastCleanUpDate},
      )
      return response.data
    } catch (error: unknown) {
      console.error('Error updating applicant cleanup date:', error)
      throw error
    }
  }

  /**
   * Update applicant cleanup date to current time
   *
   * Convenience method that sets the cleanup date to the current timestamp.
   *
   * @param applicantId - The ID of the applicant to update
   * @returns Promise with the updated applicant data
   */
  async markApplicantAsCleaned(applicantId: string): Promise<UpdateCleanupDateResponse> {
    return this.updateApplicantCleanupDate(applicantId, new Date().toISOString())
  }

  /**
   * Skip a candidate in the data cleanup process
   *
   * Marks a candidate as skipped with optional notes for follow-up.
   *
   * @param applicantId - The ID of the applicant to skip
   * @param notes - Notes explaining why the candidate was skipped
   * @returns Promise with the updated data cleanup
   */
  async skipCandidate(applicantId: string, notes: string): Promise<unknown> {
    try {
      const response = await this.post<unknown>(`/dataCleanup/skipCandidate`, {
        applicantId,
        notes,
      })
      return response.data
    } catch (error: unknown) {
      console.error('Error skipping candidate:', error)
      throw error
    }
  }

  /**
   * Get data cleanup totals
   *
   * Returns the count of applicants in each cleanup range (30, 60, 90 days)
   *
   * @param agencyId - Optional agency ID (required for General Admin role)
   * @returns Promise with the cleanup totals
   *
   * @example
   * ```typescript
   * const totals = await dataCleanupApi.getDataCleanupTotals('agency-123')
   * // { thirtyDayRangeCount: 5, sixtyDayRangeCount: 10, ninetyDayRangeCount: 15, totalCount: 30 }
   * ```
   */
  async getDataCleanupTotals(agencyId?: string): Promise<DataCleanupTotalsResponse> {
    try {
      const url = agencyId
        ? `/applicants/getDataCleanupTotals?agencyId=${agencyId}`
        : '/applicants/getDataCleanupTotals'
      const response = await this.get<DataCleanupTotalsResponse>(url)
      return response.data
    } catch (error: unknown) {
      console.error('Error fetching data cleanup totals:', error)
      throw error
    }
  }
}

// Export singleton instance
export const dataCleanupApi = new DataCleanupApiService()

export default dataCleanupApi
